/**
 * Club event / weekly meeting datetime helpers.
 * Backend stores TR wall-clock values without timezone (no Z / offset).
 * @see BackendNotes/frontend_timezone_bug.md
 */

const pad = (n) => String(n).padStart(2, '0');

/**
 * API naive string → Date from local components (not UTC instant).
 */
export function parseApiLocalDateTime(value) {
  if (value == null || value === '') return null;
  const withoutTz = String(value)
    .trim()
    .replace(/(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/i, '');
  const [datePart, timePart = '00:00:00'] = withoutTz.split('T');
  if (!datePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const timeBits = timePart.split(':');
  const hh = Number(timeBits[0] || 0);
  const mm = Number(timeBits[1] || 0);
  const ss = Number(String(timeBits[2] || '0').split('.')[0] || 0);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, hh, mm, ss);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Date → TR naive API string (e.g. 2026-05-21T19:00:00).
 */
export function toTurkeyLocalApiString(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/**
 * datetime-local value or API string → API payload string (offset-free).
 */
export function datetimeLocalToApiString(value) {
  if (value == null || value === '') return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
  const parsed = parseApiLocalDateTime(trimmed);
  return parsed ? toTurkeyLocalApiString(parsed) : null;
}

/**
 * API string → datetime-local input (YYYY-MM-DDTHH:mm).
 */
export function apiStringToDatetimeLocalInput(value) {
  const d = parseApiLocalDateTime(value);
  if (!d) return '';
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Date → YYYY-MM-DD (calendar day filters, local components).
 */
export function toTurkeyLocalDateOnly(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Month/calendar range start: first moment of day, naive API string. */
export function toTurkeyLocalApiStringStartOfDay(date) {
  if (!date) return null;
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  return toTurkeyLocalApiString(d);
}

/** Month/calendar range end: last moment of day, naive API string. */
export function toTurkeyLocalApiStringEndOfDay(date) {
  if (!date) return null;
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
  return toTurkeyLocalApiString(d);
}

const TURKEY_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

/**
 * Display event/meeting datetime (TR wall clock from API digits).
 */
export function formatTurkeyLocalDateTime(value) {
  const d = parseApiLocalDateTime(value);
  if (!d) return '-';
  const day = d.getDate();
  const month = TURKEY_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${day} ${month} ${year} - ${hours}:${minutes}`;
}

/** Date part only (no time). */
export function formatTurkeyLocalDate(value) {
  const d = parseApiLocalDateTime(value);
  if (!d) return '';
  return `${d.getDate()} ${TURKEY_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** Compact for PDF/QR: YYYY-MM-DD HH:mm */
export function formatTurkeyLocalCompact(value) {
  const d = parseApiLocalDateTime(value);
  if (!d) return '';
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Intl short display for tables. */
export function formatTurkeyLocalIntl(value) {
  const d = parseApiLocalDateTime(value);
  if (!d) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}
